import { useState } from 'react'
import { Link } from 'react-router-dom'

// サンプルデータ
const sampleRecords = [
  { 
    id: 'rec-001', 
    date: '2025-11-28', 
    type: 'patient', 
    condition: 'good',
    bowelMovements: 2,
    summary: '体調良好、便通2回',
    doctorComment: null,
  },
  { 
    id: 'rec-002', 
    date: '2025-11-27', 
    type: 'doctor', 
    condition: null,
    bowelMovements: null,
    summary: '経過観察、次回予約確認',
    doctorName: '吉岡 紘平',
  },
  { 
    id: 'rec-003', 
    date: '2025-11-26', 
    type: 'patient', 
    condition: 'normal',
    bowelMovements: 1,
    summary: '体調普通、便通1回',
    doctorComment: '引き続き経過を見守りましょう',
  },
  { 
    id: 'rec-004', 
    date: '2025-11-25', 
    type: 'patient', 
    condition: 'good',
    bowelMovements: 2,
    summary: '体調良好、便通2回',
    doctorComment: null,
  },
  { 
    id: 'rec-005', 
    date: '2025-11-24', 
    type: 'patient', 
    condition: 'normal',
    bowelMovements: 3,
    summary: '体調普通、便通3回、軽い腹痛あり',
    doctorComment: null,
  },
  { 
    id: 'rec-006', 
    date: '2025-11-23', 
    type: 'doctor', 
    condition: null,
    bowelMovements: null,
    summary: '3回目移植実施、経過良好',
    doctorName: '吉岡 紘平',
  },
]

const conditionLabels: Record<string, { label: string; emoji: string }> = {
  excellent: { label: 'とても良い', emoji: '😊' },
  good: { label: '良い', emoji: '🙂' },
  normal: { label: '普通', emoji: '😐' },
  poor: { label: '悪い', emoji: '😞' },
  very_poor: { label: 'とても悪い', emoji: '😢' },
}

export default function PatientRecords() {
  const [filterType, setFilterType] = useState<string>('all')

  const filteredRecords = sampleRecords.filter((record) => {
    if (filterType === 'all') return true
    return record.type === filterType
  })

  return (
    <div className="patient-records-page">
      <header className="page-header">
        <Link to="/patient" className="back-button">
          <span className="material-symbols-outlined">arrow_back</span>
        </Link>
        <h1 className="page-title">診療記録履歴</h1>
        <Link to="/patient/daily-record" className="new-record-button">
          <span className="material-symbols-outlined">add</span>
          今日の記録
        </Link>
      </header>

      <div className="filter-bar">
        <button
          className={`filter-button ${filterType === 'all' ? 'filter-button--active' : ''}`}
          onClick={() => setFilterType('all')}
        >
          すべて
        </button>
        <button
          className={`filter-button ${filterType === 'patient' ? 'filter-button--active' : ''}`}
          onClick={() => setFilterType('patient')}
        >
          患者入力
        </button>
        <button
          className={`filter-button ${filterType === 'doctor' ? 'filter-button--active' : ''}`}
          onClick={() => setFilterType('doctor')}
        >
          医師記録
        </button>
      </div>

      <div className="records-list">
        {filteredRecords.map((record) => (
          <div key={record.id} className={`record-card record-card--${record.type}`}>
            <div className="record-header">
              <span className="record-date">{record.date}</span>
              <span className={`record-type-badge record-type-badge--${record.type}`}>
                {record.type === 'patient' ? '患者入力' : '医師記録'}
              </span>
            </div>
            
            <div className="record-body">
              {record.type === 'patient' && record.condition && (
                <div className="record-condition">
                  <span className="condition-emoji">
                    {conditionLabels[record.condition]?.emoji}
                  </span>
                  <span className="condition-label">
                    {conditionLabels[record.condition]?.label}
                  </span>
                  {record.bowelMovements !== null && (
                    <span className="bowel-info">
                      便通: {record.bowelMovements}回
                    </span>
                  )}
                </div>
              )}
              
              <p className="record-summary">{record.summary}</p>
              
              {record.type === 'doctor' && record.doctorName && (
                <div className="doctor-info">
                  <span className="material-symbols-outlined">person</span>
                  {record.doctorName}
                </div>
              )}
              
              {record.doctorComment && (
                <div className="doctor-comment">
                  <span className="material-symbols-outlined">comment</span>
                  <span>{record.doctorComment}</span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {filteredRecords.length === 0 && (
        <div className="empty-state">
          <span className="material-symbols-outlined">description</span>
          <p>記録がありません</p>
        </div>
      )}

      <style>{`
        .patient-records-page {
          max-width: 800px;
          margin: 0 auto;
        }

        .page-header {
          display: flex;
          align-items: center;
          gap: var(--spacing-md);
          margin-bottom: var(--spacing-lg);
        }

        .back-button {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background-color: var(--background-primary);
          color: var(--text-secondary);
          box-shadow: var(--shadow-sm);
        }

        .page-title {
          font-size: 1.5rem;
          font-weight: 600;
          flex: 1;
        }

        .new-record-button {
          display: flex;
          align-items: center;
          gap: var(--spacing-xs);
          padding: var(--spacing-sm) var(--spacing-md);
          background: linear-gradient(135deg, #0891b2 0%, #06b6d4 100%);
          color: white;
          border-radius: var(--border-radius-md);
          font-size: 0.875rem;
          font-weight: 500;
          text-decoration: none;
        }

        .filter-bar {
          display: flex;
          gap: var(--spacing-sm);
          margin-bottom: var(--spacing-lg);
        }

        .filter-button {
          padding: var(--spacing-xs) var(--spacing-md);
          border: 1px solid var(--border-color);
          border-radius: var(--border-radius-md);
          background-color: var(--background-primary);
          font-size: 0.875rem;
          cursor: pointer;
          transition: all 0.2s;
        }

        .filter-button:hover {
          border-color: #0891b2;
        }

        .filter-button--active {
          background-color: #0891b2;
          color: white;
          border-color: #0891b2;
        }

        .records-list {
          display: flex;
          flex-direction: column;
          gap: var(--spacing-md);
        }

        .record-card {
          background-color: var(--background-primary);
          border-radius: var(--border-radius-lg);
          box-shadow: var(--shadow-sm);
          overflow: hidden;
        }

        .record-card--patient {
          border-left: 4px solid #0d9488;
        }

        .record-card--doctor {
          border-left: 4px solid #2563eb;
        }

        .record-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: var(--spacing-sm) var(--spacing-md);
          background-color: var(--color-solid-gray25);
          border-bottom: 1px solid var(--border-color);
        }

        .record-date {
          font-weight: 500;
        }

        .record-type-badge {
          padding: 2px 8px;
          border-radius: var(--border-radius-sm);
          font-size: 0.6875rem;
          font-weight: 500;
        }

        .record-type-badge--patient {
          background-color: #ccfbf1;
          color: #0d9488;
        }

        .record-type-badge--doctor {
          background-color: #dbeafe;
          color: #2563eb;
        }

        .record-body {
          padding: var(--spacing-md);
        }

        .record-condition {
          display: flex;
          align-items: center;
          gap: var(--spacing-sm);
          margin-bottom: var(--spacing-sm);
        }

        .condition-emoji {
          font-size: 1.5rem;
        }

        .condition-label {
          font-weight: 500;
        }

        .bowel-info {
          padding: 2px 8px;
          background-color: var(--color-solid-gray100);
          border-radius: var(--border-radius-sm);
          font-size: 0.75rem;
          color: var(--text-secondary);
        }

        .record-summary {
          color: var(--text-primary);
          margin-bottom: var(--spacing-sm);
        }

        .doctor-info {
          display: flex;
          align-items: center;
          gap: var(--spacing-xs);
          font-size: 0.875rem;
          color: var(--text-secondary);
        }

        .doctor-info .material-symbols-outlined {
          font-size: 18px;
        }

        .doctor-comment {
          display: flex;
          align-items: flex-start;
          gap: var(--spacing-sm);
          margin-top: var(--spacing-sm);
          padding: var(--spacing-sm) var(--spacing-md);
          background-color: #eff6ff;
          border-radius: var(--border-radius-md);
          font-size: 0.875rem;
          color: #2563eb;
        }

        .doctor-comment .material-symbols-outlined {
          font-size: 18px;
          flex-shrink: 0;
        }

        .empty-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: var(--spacing-xl);
          color: var(--text-secondary);
        }

        .empty-state .material-symbols-outlined {
          font-size: 48px;
          margin-bottom: var(--spacing-sm);
          opacity: 0.5;
        }
      `}</style>
    </div>
  )
}

